import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  LinearProgress,
  Tabs,
  Tab,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { Plan, PlanPhase } from '../types/generated/graphql';
import { colors } from '../theme/colors';
import { getPlanStatusColor } from '../utils/planHelpers';
import { PlanProgress } from '../utils/taskTree';
import ParticipantAvatars from './ParticipantAvatars';
import { FactsPanel } from './FactsPanel';
import {
  RESET_PLAN,
  GET_MY_ACCESSIBLE_PLANS,
  GET_PLAN,
  GET_USER_PLANS,
  GET_CONVERSATION,
} from '../graphql/queries';
import { EXECUTE_ELIGIBLE_STEPS, DELETE_PLAN } from '../graphql/mutations';
import { useToast } from '../contexts/ToastContext';

type TabValue = 'overview' | 'tasks' | 'when' | 'outputs';

interface PlanHeaderBarProps {
  plan: Plan;
  planProgress: PlanProgress;
  currentTab: TabValue;
  onTabChange: (tab: TabValue) => void;
}

function a11yProps(value: TabValue) {
  return {
    id: `plan-tab-${value}`,
    'aria-controls': `plan-tabpanel-${value}`,
  };
}

export const PlanHeaderBar: React.FC<PlanHeaderBarProps> = ({
  plan,
  planProgress,
  currentTab,
  onTabChange,
}) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { total, done, percentComplete } = planProgress;

  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  // Reset confirmation dialog state
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Reset mutation
  const [resetPlan, { loading: resetting }] = useMutation(RESET_PLAN, {
    refetchQueries: [
      { query: GET_PLAN, variables: { id: plan.id } },
      { query: GET_CONVERSATION, variables: { planId: plan.id } },
    ],
  });

  // Execute eligible steps mutation
  const [executeEligibleSteps, { loading: executing }] = useMutation(EXECUTE_ELIGIBLE_STEPS, {
    refetchQueries: [{ query: GET_PLAN, variables: { id: plan.id } }],
    onCompleted: (data) => {
      const result = data.executeEligibleSteps;
      const executedCount = result.executed.length;
      const skippedCount = result.skipped.length;
      const errorCount = result.errors.length;

      if (executedCount > 0 && errorCount === 0) {
        showToast({
          message: `Successfully executed ${executedCount} step${executedCount > 1 ? 's' : ''}`,
          severity: 'success',
        });
      } else if (executedCount > 0 && errorCount > 0) {
        showToast({
          message: `Executed ${executedCount} step${executedCount > 1 ? 's' : ''}, ${errorCount} error${errorCount > 1 ? 's' : ''}`,
          severity: 'warning',
        });
      } else if (executedCount === 0 && skippedCount > 0) {
        showToast({
          message: 'No steps eligible for auto-execution',
          severity: 'info',
        });
      } else if (errorCount > 0) {
        const errorMsg = result.errors[0]?.message || 'Unknown error';
        showToast({
          message: `Execution failed: ${errorMsg}`,
          severity: 'error',
        });
      }
    },
    onError: (error) => {
      showToast({
        message: `Execution failed: ${error.message}`,
        severity: 'error',
      });
    },
  });

  // Delete plan mutation
  const [deletePlan, { loading: deleting }] = useMutation(DELETE_PLAN, {
    refetchQueries: [{ query: GET_MY_ACCESSIBLE_PLANS }, { query: GET_USER_PLANS }],
    onCompleted: () => {
      showToast({ message: 'Plan deleted successfully', severity: 'success' });
      setDeleteDialogOpen(false);
      // Navigate to parent plan if this is a subplan, otherwise to plans list
      navigate(plan.parentPlanId ? `/plans/${plan.parentPlanId}` : '/plans');
    },
    onError: (error) => {
      showToast({ message: `Delete failed: ${error.message}`, severity: 'error' });
    },
  });

  // Calculate total duration from phases
  const totalDuration = plan.phases.reduce(
    (sum: number, phase: PlanPhase) => sum + (phase.estimatedDurationDays || 0),
    0
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: TabValue) => {
    onTabChange(newValue);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleResetClick = () => {
    handleMenuClose();
    setResetDialogOpen(true);
  };

  const handleRunAllEligible = async () => {
    handleMenuClose();
    try {
      await executeEligibleSteps({ variables: { planId: plan.id } });
    } catch (error) {
      // Error is handled by onError callback
      console.error('Failed to execute eligible steps:', error);
    }
  };

  const handleResetConfirm = async () => {
    try {
      await resetPlan({ variables: { planId: plan.id } });
      setResetDialogOpen(false);
    } catch (error) {
      console.error('Failed to reset plan:', error);
    }
  };

  const handleResetCancel = () => {
    setResetDialogOpen(false);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      showToast({ message: 'Deleting plan...', severity: 'info', autoHideDuration: 1500 });
      await deletePlan({ variables: { id: plan.id } });
    } catch (error) {
      console.error('Failed to delete plan:', error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  return (
    <Box
      sx={{
        bgcolor: colors.card,
        borderBottom: `1px solid ${colors.border}`,
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}
    >
      {/* Row 1: Top bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 3,
          py: 1.5,
          minHeight: 56,
        }}
      >
        {/* Back link - goes to parent plan if subplan, otherwise to plans list */}
        <Box
          onClick={() => navigate(plan.parentPlanId ? `/plans/${plan.parentPlanId}` : '/plans')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: colors.textSecondary,
            cursor: 'pointer',
            flexShrink: 0,
            '&:hover': {
              color: colors.blue[600],
            },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
          <Typography variant="body2" sx={{ fontWeight: 500, display: { xs: 'none', sm: 'block' } }}>
            {plan.parentPlanId ? 'Parent Plan' : 'Plans'}
          </Typography>
        </Box>

        {/* Divider */}
        <Box sx={{ width: '1px', height: 24, bgcolor: colors.gray[300], flexShrink: 0 }} />

        {/* Plan title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, minWidth: 0 }}>
          {plan.parentPlanId && (
            <Chip
              label="Subplan"
              size="small"
              sx={{
                bgcolor: colors.purple[50],
                color: colors.purple[600],
                fontWeight: 600,
                fontSize: '0.65rem',
                height: 20,
                flexShrink: 0,
              }}
            />
          )}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: colors.textPrimary,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {plan.goal || 'Untitled Plan'}
          </Typography>
        </Box>

        {/* Participant avatars */}
        {plan.participants && plan.participants.length > 0 && (
          <ParticipantAvatars
            participants={plan.participants}
            maxAvatars={3}
            size={28}
          />
        )}

        {/* Status chip */}
        <Chip
          label={plan.status}
          size="small"
          sx={{
            bgcolor: `${getPlanStatusColor(plan.status)}15`,
            color: getPlanStatusColor(plan.status),
            border: `1px solid ${getPlanStatusColor(plan.status)}30`,
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 24,
            flexShrink: 0,
          }}
        />

        {/* Facts panel - shows user decisions */}
        <FactsPanel facts={plan.facts as Record<string, unknown> | null} />

        {/* More menu */}
        <IconButton
          size="small"
          onClick={handleMenuOpen}
          sx={{
            color: colors.textSecondary,
            '&:hover': { bgcolor: colors.gray[100] },
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={menuAnchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={handleRunAllEligible} disabled={executing}>
            <ListItemIcon>
              {executing ? (
                <CircularProgress size={20} />
              ) : (
                <PlayArrowIcon fontSize="small" sx={{ color: colors.blue[600] }} />
              )}
            </ListItemIcon>
            <ListItemText>
              {executing ? 'Running...' : 'Run All Eligible Steps'}
            </ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleResetClick} sx={{ color: colors.red[600] }}>
            <ListItemIcon>
              <RestartAltIcon fontSize="small" sx={{ color: colors.red[600] }} />
            </ListItemIcon>
            <ListItemText>Reset Plan</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDeleteClick} sx={{ color: colors.red[600] }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: colors.red[600] }} />
            </ListItemIcon>
            <ListItemText>Delete Plan</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {/* Row 2: Metadata row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 3,
          pb: 1,
        }}
      >
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '0.75rem' }}>
          Created {new Date(plan.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </Typography>
        <Typography variant="body2" sx={{ color: colors.gray[400], fontSize: '0.75rem' }}>•</Typography>
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '0.75rem' }}>
          {totalDuration} days
        </Typography>
        <Typography variant="body2" sx={{ color: colors.gray[400], fontSize: '0.75rem' }}>•</Typography>
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '0.75rem' }}>
          {plan.phases.length} phases
        </Typography>
        <Typography variant="body2" sx={{ color: colors.gray[400], fontSize: '0.75rem' }}>•</Typography>
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '0.75rem' }}>
          {done}/{total} steps ({Math.round(percentComplete)}%)
        </Typography>
      </Box>

      {/* Row 3: Tabs */}
      <Box sx={{ px: 2 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="Plan detail tabs"
          sx={{
            minHeight: 40,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              minHeight: 40,
              py: 1,
              color: colors.textSecondary,
              '&.Mui-selected': {
                color: colors.blue[600],
                fontWeight: 600,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: colors.blue[600],
              height: 2,
            },
          }}
        >
          <Tab value="overview" label="Overview" {...a11yProps('overview')} />
          <Tab value="tasks" label="Tasks" {...a11yProps('tasks')} />
          <Tab value="when" label="When" {...a11yProps('when')} />
          <Tab value="outputs" label="Outputs" {...a11yProps('outputs')} />
        </Tabs>
      </Box>

      {/* Thin progress line */}
      <LinearProgress
        variant="determinate"
        value={percentComplete}
        sx={{
          height: 2,
          bgcolor: colors.gray[100],
          '& .MuiLinearProgress-bar': {
            bgcolor: colors.blue[600],
          },
        }}
      />

      {/* Reset Plan Confirmation Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={handleResetCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: colors.red[600], fontWeight: 600 }}>
          Reset Plan?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will reset all progress on this plan:
          </DialogContentText>
          <Box component="ul" sx={{ mt: 1, pl: 2, color: colors.textSecondary }}>
            <li>All steps will be set to "Not Started"</li>
            <li>All step notes and context will be cleared</li>
            <li>All conversation history will be deleted</li>
            <li>All decisions/facts will be reset</li>
            <li>All skipped steps will become active again</li>
          </Box>
          <DialogContentText sx={{ mt: 2, fontWeight: 500 }}>
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleResetCancel} disabled={resetting}>
            Cancel
          </Button>
          <Button
            onClick={handleResetConfirm}
            variant="contained"
            color="error"
            disabled={resetting}
            startIcon={resetting ? <CircularProgress size={16} color="inherit" /> : <RestartAltIcon />}
          >
            {resetting ? 'Resetting...' : 'Reset Plan'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Plan Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: colors.red[600], fontWeight: 600 }}>
          Delete Plan?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{plan.goal}"?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, fontWeight: 500 }}>
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete Plan'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default PlanHeaderBar;
